import * as React from 'react';
import { useEffect } from 'react';
import { addModifiers } from '../../../shared/helpers';
import { getDataFieldPath, getSingleDatasetPath } from '../dataExplorerRouterPaths';

import { DataFieldLabels, DATASET_NAME_TABLE_COLUMN, getReactTableColumns } from '../DatasetFieldsListTable/DatasetFieldsListTableColumns';
import cloneDeep from 'lodash/cloneDeep';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import stickybits from 'stickybits';
import { DatasetField } from '../../../shared/api/api';
import { useDataExplorerTrackCustomEvent } from '../Filters/dataExplorerAnalytics';
import { DataExplorerLink } from '../dataExplorerHistoryContext';
import { CopyToClipboard } from '../CopyToClipboard/CopyToClipboard';
import { DATA_EXPLORER_SIMULATE_CLASSNAME } from '../dataExplorerConfig';
import { CellContext } from '@tanstack/react-table';

const PivotComponent = ({ row }: { row: CellContext<DatasetField, DatasetField> }) => {
  const original = getOriginalData(row);

  const index = String(row.row.index || 0);

  useEffect(() => {
    const stickybit = stickybits(`.data-table__cell-content--sticky`, {
      useStickyClasses: true,
      useFixed: true,
      scrollEl: document.getElementsByClassName(DATA_EXPLORER_SIMULATE_CLASSNAME)?.[0] ?? window,
    });
    return () => stickybit.cleanup();
  }, [index]);

  const trackEvent = useDataExplorerTrackCustomEvent();

  return (
    <div className={addModifiers('data-table__cell-content', ['with-wrapping', 'sticky'])}>
      <DataExplorerLink
        onClick={() => {
          trackEvent('de_click_dataset');
        }}
        className={addModifiers('link', ['wrap'])}
        to={getSingleDatasetPath({ datasetId: original.dataset.id })}
      >
        {original.dataset.name}
      </DataExplorerLink>
    </div>
  );
};

export const PivotCell = (row: CellContext<DatasetField, DatasetField>) => {
  return <PivotComponent row={row} />;
};

function getOriginalData(rowData: CellContext<DatasetField, DatasetField>) {
  const row = rowData.row;
  // @ts-ignore
  const original = rowData.aggregated ? rowData.subRows?.[0]._original : row.original;
  return original;
}

export const getColumns = (trackEvent: (event: string) => void, isPyramidMultiplierVisible: boolean) => {
  return getReactTableColumns(trackEvent, isPyramidMultiplierVisible).map(column => {
    if (column.id === DATASET_NAME_TABLE_COLUMN) {
      const cloneColumn = cloneDeep(column);
      cloneColumn.cell = PivotCell;
      cloneColumn.accessor = (field: DatasetField) => field.dataset.group;
      cloneColumn.aggregationFn = 'unique';
      cloneColumn.enableGrouping = true;

      return cloneColumn;
    }

    if (column.id === 'id') {
      const cloneColumn = cloneDeep(column);

      cloneColumn.cell = rowData => {
        const original = getOriginalData(rowData);
        const { themes, new: isNew } = original;

        const isAggregated = rowData.cell.getIsAggregated();
        const fieldId = original.id as string;
        if (isAggregated) {
          return (
            <div>
              <span className={'link'}>
                <FormattedMessageHelper id="dataExplorer.allFields.seeAllFields" />
              </span>
            </div>
          );
        }

        return (
          <div className={addModifiers('data-table__cell-content', ['with-wrapping', 'break-word'])}>
            <DataExplorerLink
              onClick={() => {
                trackEvent('de_click_field');
              }}
              className={addModifiers('link', ['wrap'])}
              to={getDataFieldPath({ datasetId: original.dataset?.id, fieldId: fieldId })}
            >
              {fieldId}
            </DataExplorerLink>
            <CopyToClipboard value={fieldId} />

            <DataFieldLabels isNew={isNew} themes={themes} />
          </div>
        );
      };
      return cloneColumn;
    }
    return column;
  });
};
